import { useState, useEffect, useRef } from "react";
import "./profile.css";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { BASE_URL, getUserAddresses, createAddress, updateAddress, deleteAddress, updateUser, uploadProfileImage, getCurrentUser, fetchPincodeIndia } from "../api";
import { notifyUserUpdated } from "../utils/userNotifier";
import { toast } from "react-hot-toast";
import { FaCamera, FaPencilAlt, FaSignOutAlt, FaTrash, FaHome, FaMapMarkerAlt, FaUser, FaPlus, FaHistory } from "react-icons/fa";

const UserProfile = () => {
    // --- STATE MANAGEMENT --- //
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const DEFAULT_AVATAR_URL = "https://img.freepik.com/psd-premium/illustration-3d-commerce-electronique_169798-1843.jpg";

    const [user, setUser] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isFetchingPin, setIsFetchingPin] = useState(false);

    // State for managing all modals
    const [modalState, setModalState] = useState({
        showAccount: false, showAddress: false, showLogout: false,
        showImage: false, showConfirmAccount: false, showConfirmAddress: false,
        showConfirmDeleteAddress: false,
    });

    const [accountFormData, setAccountFormData] = useState({});
    const [addressFormData, setAddressFormData] = useState({
        addressLine1: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
        phone: "",
        is_default: false
    });
    const [addressStep, setAddressStep] = useState('details'); // 'pincode' or 'details'
    const [addressToDelete, setAddressToDelete] = useState(null);

    const token = localStorage.getItem("token");

    // --- DATA FETCHING --- //
    const fetchUserData = async () => {
        try {
            const userRes = await getCurrentUser();
            // Using the functional logic from the second file (res.data)
            if (userRes.success && userRes.data) {
                const userData = userRes.data;
                setUser(userData);
                localStorage.setItem("user", JSON.stringify(userData));
                // Notify other components in same tab and other tabs
                try { notifyUserUpdated(userData); } catch (err) { /* ignore */ }
                
                // Now fetch addresses with the user ID
                await fetchUserAddresses(userData.id || userData._id);
            } else {
                // Fallback to localStorage if API fails
                const localUser = localStorage.getItem("user");
                if (localUser) {
                    const userData = JSON.parse(localUser);
                    setUser(userData);
                    await fetchUserAddresses(userData.id || userData._id);
                } else {
                    throw new Error("User data not found");
                }
            }
        } catch (err) {
            console.error("Error fetching user data:", err);
            
            const localUser = localStorage.getItem("user");
            if (localUser) {
                const userData = JSON.parse(localUser);
                setUser(userData);
                await fetchUserAddresses(userData.id || userData._id);
            } else {
                toast.error("Failed to load user data. Please log in again.");
                navigate('/login');
            }
        }
    };

    const fetchUserAddresses = async (userId) => {
        try {
            if (!userId) {
                console.error("No user ID provided for fetching addresses");
                setAddresses([]);
                return;
            }

            const addrRes = await getUserAddresses(userId);
            if (addrRes.success && addrRes.data) {
                //  `street` and `addressLine1'
                const normalized = (addrRes.data || []).map(a => ({
                    ...a,
                    addressLine1: a.addressLine1 || a.street || '',
                    street: a.street || a.addressLine1 || ''
                }));
                setAddresses(normalized);
            } else if (addrRes.message && addrRes.message.includes("No addresses found")) {
                setAddresses([]);
            } else {
                const normalized = (addrRes.data || []).map(a => ({
                    ...a,
                    addressLine1: a.addressLine1 || a.street || '',
                    street: a.street || a.addressLine1 || ''
                }));
                setAddresses(normalized || []);
            }
        } catch (err) {
            console.error("Error fetching addresses:", err);
            if (err.message.includes("No addresses found")) {
                setAddresses([]);
            } else if (!err.message.includes("401")) {
                toast.error("Could not load addresses.");
            }
            setAddresses([]);
        }
    };

    useEffect(() => {
        if (!token) {
            toast.error("Please log in to view your profile.");
            navigate('/login');
            return;
        }
        
        const initializeData = async () => {
            setIsLoading(true);
            try {
                await fetchUserData();
            } catch (err) {
                console.error("Error initializing data:", err);
                if (err.message.includes("Authorization token required") || err.message.includes("401")) {
                    toast.error("Session expired. Please log in again."); 
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate('/login'); 
                    return;
                }
            } finally {
                setIsLoading(false);
            }
        };
        
        initializeData();
    }, [token, navigate]);

    // --- HELPER FUNCTIONS --- //
    const getFullImageUrl = (path) => {
        if (!path) return DEFAULT_AVATAR_URL;
        if (path === "default-avatar.png" || path.startsWith("imgbb.com")) return DEFAULT_AVATAR_URL;
        if (path.startsWith('http')) return path;
        return `${BASE_URL}/${path.replace(/\\/g, '/')}`;
    };
    
    const handleFormChange = (e, setter) => {
        const { name, value, type, checked } = e.target;
        setter(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const openModal = (modalName) => setModalState(prev => ({ ...prev, [modalName]: true }));
    const closeModal = (modalName) => setModalState(prev => ({ ...prev, [modalName]: false }));

    // --- API HANDLERS --- //
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.type.startsWith('image/')) {
            toast.error("Please select a valid image file.");
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image size should be less than 5MB.");
            return;
        }

        setIsUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append("profileImage", file); 
        
        try {
            const res = await uploadProfileImage(uploadFormData);
            
            const newImagePath = res.user?.profileImage || res.profileImage || file.name;
            const updatedUser = { ...user, profileImage: newImagePath }; 
            
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));
            try { notifyUserUpdated(updatedUser); } catch (err) { /* ignore */ }
            
            toast.success(res.message || "Profile image updated successfully!");

        } catch (err) {
            toast.error(err.message || "Image upload failed."); 
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = null; 
        }
    };

    const handleSaveAccount = async () => {
        closeModal('showConfirmAccount');
        setIsSaving(true);
        try {
            const userId = user.id || user._id;
            const res = await updateUser(userId, accountFormData);
            
            const updatedUserData = res.user || res;
            const updatedUser = { ...user, ...updatedUserData };
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));
            try { notifyUserUpdated(updatedUser); } catch (err) { /* ignore */ }

            closeModal('showAccount');
            
            toast.success("Profile updated successfully!"); 

        } catch (err) {
            toast.error(err.message || "Failed to update profile.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleSaveAddress = async () => {
        closeModal('showConfirmAddress');
        setIsSaving(true);
        const isUpdating = !!addressFormData._id;
        
        try {
            const userId = user.id || user._id;
            // validate fields before sending
            const validate = (data) => {
                if (!data.addressLine1 || !/\d/.test(data.addressLine1)) return 'Address Line 1 must include house/building number';
                if (!data.pincode || !/^[0-9]{6}$/.test(String(data.pincode))) return 'Pincode must be 6 digits';
                const containsNumber = s => /\d/.test(String(s || ''));
                if (containsNumber(data.city)) return 'City must not contain numbers';
                if (containsNumber(data.state)) return 'State must not contain numbers';
                if (containsNumber(data.country)) return 'Country must not contain numbers';
                return null;
            };

            const validationError = validate(addressFormData);
            if (validationError) {
                toast.error(validationError);
                setIsSaving(false);
                return;
            }

            const addressData = {
                street: addressFormData.addressLine1 || addressFormData.street || '',
                addressLine1: addressFormData.addressLine1 || addressFormData.street || '',
                city: addressFormData.city,
                state: addressFormData.state,
                pincode: addressFormData.pincode,
                country: addressFormData.country,
                phone: addressFormData.phone,
                is_default: !!addressFormData.is_default,
                user_id: userId
            };

            let res;
            if (isUpdating) {
                res = await updateAddress(addressFormData._id, addressData);
            } else {
                res = await createAddress({
                    ...addressData,
                    user_id: userId
                });
            }
            
            if (res.success || res.message) {
                await fetchUserAddresses(userId);
                closeModal('showAddress');
                toast.success(res.message || "Address saved successfully!");
            } else {
                throw new Error(res.message || "Failed to save address");
            }

        } catch (err) {
            console.error("Save address error:", err);
            toast.error(err.message || "Failed to save address.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleDeleteAddress = async () => {
        if (!addressToDelete) return;
        closeModal('showConfirmDeleteAddress');
        setIsSaving(true);
        
        try {
            const res = await deleteAddress(addressToDelete);
            
            if (res.success || res.message) {
                setAddresses(prev => prev.filter(addr => addr._id !== addressToDelete));
                toast.success(res.message || "Address deleted successfully!");
            } else {
                throw new Error(res.message || "Failed to delete address");
            }

        } catch (err) {
            console.error("Delete address error:", err);
            
            if (err.message.includes("404") || err.message.includes("not found")) {
                const userId = user.id || user._id;
                await fetchUserAddresses(userId);
                toast.success("Address deleted successfully!");
            } else {
                toast.error(err.message || "Failed to delete address.");
            }
        } finally {
            setIsSaving(false);
            setAddressToDelete(null);
        }
    };

    const handleSetDefaultAddress = async (addressId) => {
        try {
            const address = addresses.find(addr => addr._id === addressId);
            if (!address) return;

            const res = await updateAddress(addressId, {
                ...address,
                is_default: true
            });

            if (res.success || res.message) {
                const updatedAddresses = addresses.map(addr => ({
                    ...addr,
                    is_default: addr._id === addressId
                }));
                setAddresses(updatedAddresses);
                toast.success("Default address updated successfully!");
            } else {
                throw new Error(res.message || "Failed to set default address");
            }
        } catch (err) {
            console.error("Set default address error:", err);
            toast.error(err.message || "Failed to set default address.");
        }
    };

    const performLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        toast.success("Logged out successfully!");
        navigate("/login");
    };

    // --- MODAL OPEN/CLOSE HANDLERS --- //
    const openAccountModal = () => {
        if (!user) {
            toast.error("User data not loaded. Please refresh the page.");
            return;
        }
        setAccountFormData({ 
            firstName: user.firstName || '', 
            lastName: user.lastName || '', 
            phone: user.phone || '',
            email: user.email || ''
        });
        openModal('showAccount');
    };

    const openAddressModalHandler = (address = {}) => {
        if (!user) {
            toast.error("User data not loaded. Please refresh the page.");
            return;
        }
        // Prefill modal with normalized fields (support both `street` and `addressLine1`)
        const isNew = !address || Object.keys(address).length === 0;
        setAddressFormData({ 
            _id: address._id || address.id || undefined,
            addressLine1: address.addressLine1 || address.street || "",
            street: address.street || address.addressLine1 || "",
            city: address.city || "",
            state: address.state || "",
            pincode: address.pincode || "",
            country: address.country || "India",
            phone: address.phone || user.phone || "",
            is_default: !!address.is_default
        });
        // pin code handlesr
        setAddressStep(isNew ? 'pincode' : 'details');
        openModal('showAddress');
    };

    // Fetch pincode details and autofill city/state
    const fetchPincodeAndFill = async (pincode) => {
        if (!pincode || !/^[0-9]{3,6}$/.test(String(pincode))) {
            toast.error('Enter a valid PIN code (6 digits)');
            return;
        }
        setIsFetchingPin(true);
        try {
            const info = await fetchPincodeIndia(pincode);
            setAddressFormData(prev => ({ ...prev, city: info.district || prev.city, state: info.state || prev.state, country: 'India' }));
            toast.success('Auto-filled city/state from PIN');
            // Move to details step so other inputs are shown
            setAddressStep('details');
        } catch (err) {
            toast.error(err.message || 'Could not fetch PIN details');
        } finally {
            setIsFetchingPin(false);
        }
    };

    const openDeleteAddressModal = (addressId) => {
        setAddressToDelete(addressId);
        openModal('showConfirmDeleteAddress');
    };

    // Format address for display
    const formatAddress = (address) => {
        const parts = [
            address.addressLine1 || address.street,
            address.city,
            address.state,
            address.pincode,
            address.country
        ].filter(part => part && part.trim() !== '');
        
        return parts.join(', ');
    };

    if (isLoading) {
        return (
            <div className="profile-container d-flex align-items-center justify-content-center">
                <div className="loading-spinner-large"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="profile-container d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <h4 className="text-white mb-3">Unable to load profile</h4>
                    <Button variant="light" onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-container d-flex align-items-center justify-content-center">
            <div className="container-fluid">
                <Row className="justify-content-center">
                    <Col xs={12} md={10} lg={8} xl={7}>
                        <div className="card user-card-full">
                            <Row className="g-0">
                                {/* Left Panel - Profile Image */}
                                <Col md={4} className="user-profile-panel">
                                    <div className="avatar-section">
                                        <img 
                                            src={getFullImageUrl(user.profileImage)} 
                                            className="img-radius" 
                                            alt="User Profile" 
                                            onClick={() => openModal('showImage')}
                                            onError={(e) => {
                                                e.target.src = DEFAULT_AVATAR_URL;
                                            }}
                                        />
                                        <input 
                                            type="file" 
                                            ref={fileInputRef} 
                                            onChange={handleImageUpload} 
                                            accept="image/*" 
                                            style={{ display: 'none' }} 
                                        />
                                        <button 
                                            className="edit-avatar-btn" 
                                            onClick={() => fileInputRef.current.click()} 
                                            disabled={isUploading}
                                        >
                                            <FaCamera size={14} />
                                        </button>
                                        {isUploading && (
                                            <div className="loading-overlay">
                                                <div className="loading-spinner"></div>
                                            </div>
                                        )}
                                    </div>
                                    <h6 className="user-name">{user.firstName} {user.lastName}</h6>
                                    <p className="user-tagline">Aadvi - The Designer Studio</p>
                                    <p className="user-email text-light">{user.email}</p>
                                    
                                    {/* Quick Stats */}
                                    {/* <div className="quick-stats mt-4">
                                        <div className="stat-item">
                                            <FaMapMarkerAlt className="stat-icon" />
                                            <span className="stat-count">{addresses.length}</span>
                                            <span className="stat-label">Addresses</span>
                                        </div>
                                    </div> */}
                                </Col>

                                {/* Right Panel - User Info and Addresses */}
                                <Col md={8} className="info-panel info-panel-wrapper" >
                                    <h6 className="info-section-header">
                                        <FaUser className="me-2 section-icon" />
                                        Account Information
                                    </h6>
                                    <Row className="info-item mb-4" style={{marginLeft:'5%'}}>
                                        <Col sm={6}>
                                            <p className="info-label">
                                                <FaUser className="me-1" size={12} />
                                                Full Name
                                            </p>
                                            <h6 className="info-value">{user.firstName} {user.lastName}</h6>
                                        </Col>
                                        <Col sm={6}>
                                            <p className="info-label">
                                                <FaHome className="me-1" size={12} />
                                                Phone
                                            </p>
                                            <h6 className="info-value">{user.phone || 'Not provided'}</h6>
                                        </Col>
                                    </Row>

                                    <h6 className="info-section-header mt-4">
                                        <FaMapMarkerAlt className="me-2 section-icon" />
                                        Shipping Addresses
                                        <span className="address-count-badge">{addresses.length}</span>
                                    </h6>
                                    
                                    {addresses.length > 0 ? (
                                        <div className="addresses-list" style={{display:'flex', justifyContent:'center', flexDirection:'column'}}>
                                            {addresses.map(addr => (
                                                <div key={addr._id} className={`address-card ${addr.is_default ? 'default-address' : ''}`} style={{maxWidth:'90%', margin:'10px auto'}}>
                                                    <div className="address-content" >
                                                        {addr.is_default && (
                                                            <span className="default-badge">
                                                                <FaHome className="me-1" size={10} />
                                                                Default
                                                            </span>
                                                        )}
                                                        <p className="mb-1 address-text" >{formatAddress(addr)}</p>
                                                        {addr.phone && (
                                                            <p className="mb-0 text-muted small">
                                                                <FaHome className="me-1" size={10} />
                                                                Phone: {addr.phone}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="address-actions">
                                                        {!addr.is_default && (
                                                            <button 
                                                                className="btn-icon set-default" 
                                                                onClick={() => handleSetDefaultAddress(addr._id)}
                                                                title="Set as default"
                                                            >
                                                                <FaHome className="me-1" size={12} />
                                                                Set Default
                                                            </button>
                                                        )}
                                                        <button 
                                                            className="btn-icon edit" 
                                                            onClick={() => openAddressModalHandler(addr)}
                                                            title="Edit address"
                                                        >
                                                            <FaPencilAlt size={14} />
                                                        </button>
                                                        <button 
                                                            className="btn-icon delete" 
                                                            onClick={() => openDeleteAddressModal(addr._id)}
                                                            title="Delete address"
                                                        >
                                                            <FaTrash size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 empty-address">
                                            <FaMapMarkerAlt size={48} className="empty-icon mb-3" />
                                            <p className="empty-text">No addresses saved yet.</p>
                                            <p className="empty-subtext">Add your first address to make checkout easier!</p>
                                        </div>
                                    )}
                                    <div style={{maxWidth:'80%', margin:'0 auto', display:'flex', justifyContent:'center'}}>
                                    
                                    <Button 
                                        variant="primary" 
                                        className="mt-3 add-address-btn"
                                        onClick={() => openAddressModalHandler({})}
                                        
                                    >
                                        <FaPlus className="me-2" />
                                        Add New Address
                                    </Button>
                                    </div>

                                    <div className="action-buttons-container mt-4 pt-4">
                                        <div className="d-flex flex-wrap gap-2" style={{margin:'0 auto  10px auto', display:'flex', justifyContent:'center', maxWidth:'95%'}}>
                                            <Link to="/orderhistory" className="btn custom-btn order-history-btn">
                                                <FaHistory className="me-2" />
                                                Order History
                                            </Link>
                                            <Button className="custom-btn-primary manage-account-btn" onClick={openAccountModal}>
                                                <FaUser className="me-2" />
                                                Manage Account
                                            </Button>
                                            <Button variant="outline-danger" className="logout-btn" onClick={() => openModal('showLogout')}>
                                                <FaSignOutAlt className="me-2" />
                                                Log Out
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* --- MODALS --- */}

            {/* Account Management Modal with Enhanced Particle Animation */}
            <Modal show={modalState.showAccount} onHide={() => closeModal('showAccount')} centered className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom" style={{ background: 'linear-gradient(135deg, #82287A 0%, #B35AAE 100%)', border: 'none' }}>
                    <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>
                        <FaUser className="me-2" />
                        Manage Account
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ 
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #82287A 0%, #B35AAE 100%)',
                    minHeight: '300px',
                    padding: '20px'
                }}> 
                    {/* Form Content */}
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>First Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="firstName" 
                                    value={accountFormData.firstName || ''} 
                                    onChange={(e) => handleFormChange(e, setAccountFormData)} 
                                    style={{ 
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        border: '2px solid rgba(255, 255, 255, 0.5)',
                                        borderRadius: '8px',
                                        color: '#333',
                                        fontWeight: '500',
                                        padding: '12px 15px',
                                        fontSize: '14px'
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Last Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="lastName" 
                                    value={accountFormData.lastName || ''} 
                                    onChange={(e) => handleFormChange(e, setAccountFormData)} 
                                    style={{ 
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        border: '2px solid rgba(255, 255, 255, 0.5)',
                                        borderRadius: '8px',
                                        color: '#333',
                                        fontWeight: '500',
                                        padding: '12px 15px',
                                        fontSize: '14px'
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Phone</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="phone" 
                                    value={accountFormData.phone || ''} 
                                    onChange={(e) => handleFormChange(e, setAccountFormData)} 
                                    style={{ 
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        border: '2px solid rgba(255, 255, 255, 0.5)',
                                        borderRadius: '8px',
                                        color: '#333',
                                        fontWeight: '500',
                                        padding: '12px 15px',
                                        fontSize: '14px'
                                    }}
                                />
                            </Form.Group>
                        </Form>
                    </div>
                </Modal.Body>
                <Modal.Footer className="modal-footer-custom" style={{ background: 'rgba(130, 40, 122, 0.95)', backdropFilter: 'blur(10px)', border: 'none' }}>
                    <Button variant="outline-light" onClick={() => closeModal('showAccount')} className="cancel-btn text-light" style={{ borderRadius: '8px', padding: '8px 20px' }}>
                        Cancel
                    </Button>
                    <Button variant="light" onClick={() => openModal('showConfirmAccount')} disabled={isSaving} className="confirm-btn" style={{ color: '#daccd8ff', fontWeight: 'bold', borderRadius: '8px', padding: '8px 20px' }}>
                        {isSaving ? 'Updating...' : 'Update Profile'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Confirm Account Update Modal */}
            <Modal show={modalState.showConfirmAccount} onHide={() => closeModal('showConfirmAccount')} centered className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom">
                    <Modal.Title>Confirm Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to update your profile information?</Modal.Body>
                <Modal.Footer className="modal-footer-custom">
                    <Button variant="outline-secondary" onClick={() => closeModal('showConfirmAccount')} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveAccount} disabled={isSaving} className="confirm-btn">
                        {isSaving ? 'Updating...' : 'Yes, Update'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Address Management Modal with Enhanced Particle Animation */}
            <Modal show={modalState.showAddress} onHide={() => closeModal('showAddress')} centered size="lg" className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom" style={{ background: 'linear-gradient(135deg, #82287A 0%, #B35AAE 100%)', border: 'none' }}>
                    <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>
                        <FaMapMarkerAlt className="me-2" />
                        {addressFormData._id ? "Edit Address" : "Add New Address"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ 
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #82287A 0%, #B35AAE 100%)',
                    minHeight: '400px',
                    padding: '20px'
                }}>
                    {/* Particle animation removed from address modal for performance */}
                    
                    {/* Form Content */}
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        {addressStep === 'pincode' ? (
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Enter Pincode to continue</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pincode"
                                        value={addressFormData.pincode || ''}
                                        onChange={e => handleFormChange(e, setAddressFormData)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchPincodeAndFill(addressFormData.pincode); } }}
                                        placeholder="Pincode"
                                        required
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            border: '2px solid rgba(255, 255, 255, 0.5)',
                                            borderRadius: '8px',
                                            color: '#333',
                                            fontWeight: '500',
                                            padding: '12px 15px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </Form.Group>
                                <div className="d-flex gap-2">
                                    <Button variant="light" onClick={() => closeModal('showAddress')} className="cancel-btn">Cancel</Button>
                                    <Button variant="primary" onClick={() => fetchPincodeAndFill(addressFormData.pincode)} className="confirm-btn">Continue</Button>
                                </div>
                            </Form>
                        ) : (
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Address Line 1 *</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="addressLine1" 
                                        value={addressFormData.addressLine1 || ''} 
                                        onChange={e => handleFormChange(e, setAddressFormData)}
                                        placeholder="House/Flat No, Building, Street"
                                        required
                                        style={{ 
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            border: '2px solid rgba(255, 255, 255, 0.5)',
                                            borderRadius: '8px',
                                            color: '#333',
                                            fontWeight: '500',
                                            padding: '12px 15px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>City *</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="city" 
                                                value={addressFormData.city || ''} 
                                                onChange={e => handleFormChange(e, setAddressFormData)}
                                                placeholder="City"
                                                required
                                                style={{ 
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    border: '2px solid rgba(255, 255, 255, 0.5)',
                                                    borderRadius: '8px',
                                                    color: '#333',
                                                    fontWeight: '500',
                                                    padding: '12px 15px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>State *</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="state" 
                                                value={addressFormData.state || ''} 
                                                onChange={e => handleFormChange(e, setAddressFormData)}
                                                placeholder="State"
                                                required
                                                style={{ 
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    border: '2px solid rgba(255, 255, 255, 0.5)',
                                                    borderRadius: '8px',
                                                    color: '#333',
                                                    fontWeight: '500',
                                                    padding: '12px 15px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Pincode *</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="pincode" 
                                                value={addressFormData.pincode || ''} 
                                                onChange={e => handleFormChange(e, setAddressFormData)}
                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fetchPincodeAndFill(addressFormData.pincode); } }}
                                                placeholder="Pincode"
                                                required
                                                style={{ 
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    border: '2px solid rgba(255, 255, 255, 0.5)',
                                                    borderRadius: '8px',
                                                    color: '#333',
                                                    fontWeight: '500',
                                                    padding: '12px 15px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Country *</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="country" 
                                                value={addressFormData.country || 'India'} 
                                                onChange={e => handleFormChange(e, setAddressFormData)}
                                                required
                                                style={{ 
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    border: '2px solid rgba(255, 255, 255, 0.5)',
                                                    borderRadius: '8px',
                                                    color: '#333',
                                                    fontWeight: '500',
                                                    padding: '12px 15px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>Phone Number *</Form.Label>
                                    <Form.Control 
                                        type="tel" 
                                        name="phone" 
                                        value={addressFormData.phone || ''} 
                                        onChange={e => handleFormChange(e, setAddressFormData)}
                                        placeholder="Phone number for delivery"
                                        required
                                        style={{ 
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            border: '2px solid rgba(255, 255, 255, 0.5)',
                                            borderRadius: '8px',
                                            color: '#333',
                                            fontWeight: '500',
                                            padding: '12px 15px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="checkbox" 
                                        name="is_default" 
                                        label="Set as default address"
                                        checked={addressFormData.is_default || false}
                                        onChange={e => handleFormChange(e, setAddressFormData)}
                                        style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}
                                    />
                                </Form.Group>
                            </Form>
                        )}
                    </div>

                    {/* Inline particle keyframes removed */}
                </Modal.Body>
                <Modal.Footer className="modal-footer-custom" style={{ background: 'rgba(130, 40, 122, 0.95)', backdropFilter: 'blur(10px)', border: 'none' }}>
                    <Button variant="outline-light" onClick={() => closeModal('showAddress')} className="cancel-btn text-light" style={{ borderRadius: '8px', padding: '8px 20px' }}>
                        Cancel
                    </Button>
                    <Button variant="light" onClick={() => openModal('showConfirmAddress')} disabled={isSaving} className="confirm-btn text-light" style={{ color: '#82287A', fontWeight: 'bold', borderRadius: '8px', padding: '8px 20px' }}>
                        {addressFormData._id ? 'Update Address' : 'Save Address'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Confirm Address Save Modal */}
            <Modal show={modalState.showConfirmAddress} onHide={() => closeModal('showConfirmAddress')} centered className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom">
                    <Modal.Title>Confirm Save</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to save this address?</Modal.Body>
                <Modal.Footer className="modal-footer-custom">
                    <Button variant="outline-secondary" onClick={() => closeModal('showConfirmAddress')} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveAddress} disabled={isSaving} className="confirm-btn">
                        {isSaving ? 'Saving...' : 'Yes, Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Confirm Address Deletion Modal */}
            <Modal show={modalState.showConfirmDeleteAddress} onHide={() => closeModal('showConfirmDeleteAddress')} centered className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom">
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this address? This action cannot be undone.</Modal.Body>
                <Modal.Footer className="modal-footer-custom">
                    <Button variant="outline-secondary" onClick={() => closeModal('showConfirmDeleteAddress')} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteAddress} disabled={isSaving} className="delete-confirm-btn">
                        {isSaving ? 'Deleting...' : 'Yes, Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Logout Confirmation Modal */}
            <Modal show={modalState.showLogout} onHide={() => closeModal('showLogout')} centered className="custom-modal">
                <Modal.Header closeButton className="modal-header-custom">
                    <Modal.Title>Confirm Logout</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to log out of your account?</Modal.Body>
                <Modal.Footer className="modal-footer-custom">
                    <Button variant="outline-secondary" onClick={() => closeModal('showLogout')} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={performLogout} className="logout-confirm-btn">
                        <FaSignOutAlt className="me-2" />
                        Log Out
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Profile Image Modal */}
            <Modal show={modalState.showImage} onHide={() => closeModal('showImage')} centered size="lg" className="image-modal custom-modal">
                <Modal.Header closeButton className="modal-header-custom">
                    <Modal.Title>Profile Picture</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img 
                        src={getFullImageUrl(user.profileImage)} 
                        alt="Full Size Profile" 
                        className="full-size-image"
                        onError={(e) => {
                            e.target.src = DEFAULT_AVATAR_URL;
                        }}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default UserProfile;